/**
 * MotionFilters — the shared filter row for BOTH Motions-page tabs (Figma
 * 1956:24237 / 1920:12350): Industry · Use Case · Motion · Topic · Asset.
 *
 * Each is a Cloudscape Multiselect with a floating label on the field's top
 * border (qingfeng AgentHubPortal pattern) + enableSelectAll ("全选 / 全不选") +
 * filteringType="auto" (type-to-filter). Fully controlled by the parent; this
 * component OWNS option fetching (GetSolutionMapAll for Industry/UC, ListTopics
 * for Topic, ListAssets for Asset) so both tabs share it identically.
 *
 * Values are Cloudscape options; the parent maps them to backend params:
 *   - Industry / Use Case → industries / useCases (string values)
 *   - Topic → subTopicIds (numeric subTopicId, option value is String(id))
 *   - Asset → assetIds (assetId)
 *   - Motion → List tab filters loaded rows locally; Pipeline tab sends motionIds.
 *     Motion OPTIONS differ per tab (List = loaded Motions; Pipeline = ListMotions),
 *     so the parent supplies `motionOptions`; everything else is self-fetched.
 *
 * Use Case options are scoped to the chosen Industries (a UC belongs to an
 * industry); selected UCs out of scope are pruned by the parent-held value +
 * the onUseCasesChange callback here. Motion options can likewise SHRINK on the
 * Motion List tab (they are the loaded ListMotions rows, which refetch on a
 * server filter change), so orphaned Motion selections are pruned the same way.
 */

import { useEffect, useMemo, useState } from 'react';
import { Multiselect, type MultiselectProps, Select, type SelectProps } from '@amzn/awsui-components-react';
import {
  getIvtClient,
  GetSolutionMapAllCommand,
  ListAssetsCommand,
  type AssetSummary,
} from '../../../client/IvtHubClient';
import { topicClient } from '../../../client/topicClient';
import { scopeIndustriesToAllowed } from '../../../client/permissionClient';
import FloatingLabelField from '../../../components/FloatingLabelField/FloatingLabelField';
import { isNew } from '../../AssetsPage/utils/assetGrouping';
import './MotionFilters.css';

export type MotionFilterOption = MultiselectProps.Option;

const SELECT_ALL_I18N: MultiselectProps.I18nStrings = { selectAllText: '全选 / 全不选' };

interface MotionFiltersProps {
  industries: readonly MotionFilterOption[];
  onIndustriesChange: (v: readonly MotionFilterOption[]) => void;
  /** The permission-allowed industries (SETTLED set); the dropdown is scoped to
   *  their intersection with the real industry universe. Empty = no permission →
   *  no industry options (fail-closed). */
  allowedIndustries?: string[] | null;
  /** True once the permission fetch has succeeded (READY). Until then the industry
   *  dropdown is empty so a not-yet-scoped full list can't briefly expose disallowed
   *  industries. Defaults true so the component works standalone / in tests. */
  permissionReady?: boolean;
  useCases: readonly MotionFilterOption[];
  onUseCasesChange: (v: readonly MotionFilterOption[]) => void;
  /** Motion options are supplied by the parent (differ per tab). */
  motionOptions: MotionFilterOption[];
  motions: readonly MotionFilterOption[];
  onMotionsChange: (v: readonly MotionFilterOption[]) => void;
  subTopics: readonly MotionFilterOption[];
  onSubTopicsChange: (v: readonly MotionFilterOption[]) => void;
  assets: readonly MotionFilterOption[];
  onAssetsChange: (v: readonly MotionFilterOption[]) => void;
  // NOTE: SFDC Stage AND Engagement Depth Score both moved OUT of this row into the
  // Pipeline View's PipelinePropertyFilter (each is now a field of the SFDC property
  // tree — SFDC_STAGE / PIPELINE_SCORE; see PipelinePropertyFilter.tsx / DAT-8462).
  /** Motion-claim filter — Pipeline tab ONLY (tagged pipeline can be motion-claimed
   *  OR tag-only). Omit the handler to hide the field. A single-select tri-state:
   *  undefined = All / true = only opps WITH a Motion claim / false = only tag-only
   *  (no claim) — maps to the backend `hasMotion` param. */
  hasMotion?: boolean;
  onHasMotionChange?: (v: boolean | undefined) => void;
}

// Motion-claim tri-state (Pipeline tab). value maps to the backend hasMotion param:
// 'all' → undefined (no constraint) / 'with' → true / 'without' → false.
const HAS_MOTION_OPTIONS: SelectProps.Option[] = [
  { value: 'all', label: '全部' },
  { value: 'with', label: '有关联 Motion' },
  { value: 'without', label: '无关联 Motion' },
];
const HAS_MOTION_TO_VALUE: Record<string, boolean | undefined> = { all: undefined, with: true, without: false };
function hasMotionOptionId(v: boolean | undefined): string {
  return v === true ? 'with' : v === false ? 'without' : 'all';
}

// Filter-row cell: the shared FloatingLabelField wrapped with this row's flex-share
// layout (.ivt-motion-filters__cell — flex:1 / min-width). The notch/label markup +
// typography live in the shared component so all IVT filters stay pixel-consistent.
function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <FloatingLabelField label={label} className="ivt-motion-filters__cell">
      {children}
    </FloatingLabelField>
  );
}

export default function MotionFilters({
  industries,
  onIndustriesChange,
  allowedIndustries,
  permissionReady = true,
  useCases,
  onUseCasesChange,
  motionOptions,
  motions,
  onMotionsChange,
  subTopics,
  onSubTopicsChange,
  assets,
  onAssetsChange,
  hasMotion,
  onHasMotionChange,
}: MotionFiltersProps) {
  const [industryOpts, setIndustryOpts] = useState<MotionFilterOption[]>([]);
  const [ucByIndustry, setUcByIndustry] = useState<Map<string, string[]>>(new Map());
  // Gate the UC prune on the initial fetch: before GetSolutionMapAll resolves,
  // ucByIndustry is empty → ucOptions is [] → an unguarded prune would wipe ALL
  // pre-existing UC selections (e.g. a future deep-link / restored filter). Only
  // prune once the option source has actually loaded. (AutoSDE CR-286021738 r1.)
  const [ucLoaded, setUcLoaded] = useState(false);
  const [topicGroups, setTopicGroups] = useState<MultiselectProps.OptionGroup[]>([]);
  // Full asset rows (NOT just {value,label}) — we need solutionMapLinks/topicLinks
  // to scope the Asset options by chosen Industry / UC / Topic (assetOpts below).
  const [assetRows, setAssetRows] = useState<AssetSummary[]>([]);

  // Industry + Use Case options (GetSolutionMapAll).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new GetSolutionMapAllCommand({}));
        if (cancelled) return;
        const all = out.industries ?? [];
        setIndustryOpts(
          all.map((i) => i.industry).filter((n): n is string => !!n).sort((a, b) => a.localeCompare(b, 'en')).map((i) => ({ label: i, value: i })),
        );
        const m = new Map<string, string[]>();
        for (const i of all) {
          if (!i.industry) continue;
          m.set(i.industry, [...new Set((i.entries ?? []).map((e) => e.useCase).filter((u): u is string => !!u))].sort((a, b) => a.localeCompare(b, 'en')));
        }
        setUcByIndustry(m);
        setUcLoaded(true);
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Topic options — grouped by Topic; option value = String(subTopicId).
  useEffect(() => {
    let cancelled = false;
    topicClient
      .list()
      .then(({ topics }) => {
        if (cancelled) return;
        setTopicGroups(
          topics
            .map((t) => ({
              label: t.name ?? '',
              options: (t.instances ?? [])
                .filter((ti): ti is typeof ti & { subTopicId: number; subTopicName: string } => typeof ti.subTopicId === 'number' && !!ti.subTopicName)
                .map((ti) => ({ label: ti.subTopicName, value: String(ti.subTopicId) })),
            }))
            .filter((g) => g.options.length > 0),
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Asset rows (ListAssets) — kept whole so the option builder can scope them by
  // chosen Industry / UC / Topic. ALL origins (no origin filter): tagged pipeline is
  // tag-anchored to any asset (incubated / legacy / partner / global), not just the
  // incubated slice — so the filter's asset universe must cover them all, else a
  // tag-anchored opp's asset couldn't be picked. Incubated assets get a "New"
  // labelTag below (mirrors the Assets tree's New tag).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const out = await getIvtClient().send(new ListAssetsCommand({}));
        if (cancelled) return;
        setAssetRows((out.assets ?? []).filter((a): a is AssetSummary & { assetId: string } => !!a.assetId));
      } catch {
        /* non-blocking */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Scope industry options to the user's allowed industries (permission-based).
  // INTERSECT the real SolutionMap industries (industryOpts) with the allowed set:
  // the Permission Service sh_l4 set contains non-industry codes (CROSS, GFD AM,
  // GFD CAC) that would otherwise show as ghost dropdown options matching no
  // Motion. fail-open (null/empty allowed) keeps the full SolutionMap set.
  //
  // GATE on permissionReady: until permission SETTLES, expose NO industry
  // options (empty dropdown, shows the loading/empty state). Otherwise the full
  // SolutionMap set would briefly be selectable and a user could pick a disallowed
  // industry (e.g. ECOM) in that window — that selection would then stick (the
  // prune below keeps only allowed-scoped ones once permission lands, but the
  // fetch/leak already happened). No options until we know what's allowed.
  const filteredIndustryOpts = useMemo<MotionFilterOption[]>(() => {
    if (!permissionReady) return [];
    // allowedIndustries UNDEFINED = parent passed no scope (standalone) → show all;
    // a real array (incl. empty = no permission → no options) is scoped fail-closed.
    if (allowedIndustries == null) return industryOpts;
    const allowedSet = new Set(
      scopeIndustriesToAllowed(
        industryOpts.map((o) => o.value).filter((v): v is string => !!v),
        allowedIndustries,
      ),
    );
    return industryOpts.filter((o) => o.value && allowedSet.has(o.value));
  }, [industryOpts, allowedIndustries, permissionReady]);

  // UC options scoped to chosen industries (union if none chosen).
  const ucOptions = useMemo<MotionFilterOption[]>(() => {
    const chosen = new Set(industries.map((o) => o.value));
    const names = chosen.size > 0 ? [...chosen].flatMap((ind) => ucByIndustry.get(ind ?? '') ?? []) : [...ucByIndustry.values()].flat();
    return [...new Set(names)].sort((a, b) => a.localeCompare(b, 'en')).map((uc) => ({ label: uc, value: uc }));
  }, [ucByIndustry, industries]);

  // Prune out-of-scope UC selections after an industry change. Gated on ucLoaded
  // so the empty-Map mount state can't wipe pre-existing selections.
  useEffect(() => {
    if (!ucLoaded) return;
    const valid = new Set(ucOptions.map((o) => o.value));
    const kept = useCases.filter((o) => valid.has(o.value));
    if (kept.length !== useCases.length) onUseCasesChange(kept);
  }, [ucLoaded, ucOptions, useCases, onUseCasesChange]);

  // Asset options scoped to the chosen Industry / UC / Topic (each dimension: none
  // chosen = no constraint; union-if-none, same as Industry→UC). An asset qualifies
  // only if it satisfies EVERY chosen dimension (per-dimension AND, mirroring the
  // backend's AND-across-filters). Matching reads the concrete LINK rows, not the
  // flat AssetSummary.industry/.useCase/.topic (those lag / are single-valued):
  //   - Industry / UC → solutionMapLinks[] (industry-anchored assets). A chosen
  //     Industry with no matching link excludes topic-anchored assets (industry
  //     null) — correct: Industry and Topic are parallel dimensions, so picking an
  //     Industry means "industry-anchored assets in it", not topic-anchored ones.
  //   - Topic (sub-topic) → topicLinks[].subTopicId (topic-anchored assets); the
  //     option value is String(subTopicId), so compare String(link.subTopicId).
  // Assets are UC/Topic-EXCLUSIVE by construction (Create is a UC-or-Topic toggle),
  // so choosing Industry/UC and Topic together yields an empty picker — a genuinely
  // contradictory request, not a bug.
  const assetOpts = useMemo<MotionFilterOption[]>(() => {
    const chosenInd = new Set(industries.map((o) => o.value));
    const chosenUc = new Set(useCases.map((o) => o.value));
    const chosenSub = new Set(subTopics.map((o) => o.value));
    const matches = assetRows.filter((a) => {
      const smLinks = a.solutionMapLinks ?? [];
      const tLinks = a.topicLinks ?? [];
      if (chosenInd.size > 0 && !smLinks.some((l) => l.industry && chosenInd.has(l.industry))) return false;
      if (chosenUc.size > 0 && !smLinks.some((l) => l.useCase && chosenUc.has(l.useCase))) return false;
      if (chosenSub.size > 0 && !tLinks.some((l) => l.subTopicId != null && chosenSub.has(String(l.subTopicId)))) return false;
      return true;
    });
    return matches
      .map((a) => {
        const name = a.name ?? a.useCase ?? (a.assetId as string);
        // `label` is kept (drives filtering + the selected-token text). Incubated
        // (New) assets additionally render a GREEN "New" badge in the dropdown row
        // via labelContent — same teal badge as the Assets tree's New tag (reuse the
        // .ivt-asset-tree__new-tag class), so the two surfaces look identical.
        const opt: MotionFilterOption = { value: a.assetId as string, label: name };
        if (isNew(a)) {
          opt.labelContent = (
            <span>
              <span className="ivt-motion-filters__new-tag">New</span>
              {name}
            </span>
          );
        }
        return opt;
      })
      .sort((x, y) => (x.label ?? '').localeCompare(y.label ?? '', 'en'));
  }, [assetRows, industries, useCases, subTopics]);

  // Prune orphaned Motion selections when motionOptions shrink. On the Motion
  // List tab the options ARE the loaded ListMotions rows, which refetch (and can
  // drop a Motion) whenever a server filter changes — without this, a selected
  // Motion that's no longer an option stays a "ghost" token filtering to nothing.
  // (The deleted MotionFilterBar had this; it must not be lost in the refactor.)
  // Options come from a prop with no explicit "loaded" signal, so skip when it is
  // empty: that's indistinguishable from "not fetched yet" and pruning against an
  // empty set would wipe every selection at mount (AutoSDE CR-286021738 r1). A
  // genuinely empty result means the list is empty anyway, so nothing is lost.
  useEffect(() => {
    if (motionOptions.length === 0) return;
    const valid = new Set(motionOptions.map((o) => o.value));
    const kept = motions.filter((o) => valid.has(o.value));
    if (kept.length !== motions.length) onMotionsChange(kept);
  }, [motionOptions, motions, onMotionsChange]);

  // Prune orphaned Industry / Topic / Asset selections the same way. This matters
  // now that selections PERSIST across sessions (sessionStorage): a stored value
  // whose option was since deleted/renamed backend-side would otherwise render as
  // a "ghost" token with no ✕ (Cloudscape hides remove on unmatched options) —
  // unclearable, and sent to the backend as a dead filter. Each guards on
  // "options non-empty" so it can't wipe a restored selection before options load.
  // Prune against the PERMISSION-SCOPED options (filteredIndustryOpts), not the
  // full industryOpts — so a disallowed industry that was selected during the
  // pre-permission window (or restored from sessionStorage) is dropped once
  // permission lands, instead of sticking as a live cross-scope filter. Guard:
  // only prune after permission resolves AND options are non-empty, so a restored
  // selection isn't wiped before either lands (same mount-race guard as before).
  useEffect(() => {
    if (!permissionReady || filteredIndustryOpts.length === 0) return;
    const valid = new Set(filteredIndustryOpts.map((o) => o.value));
    const kept = industries.filter((o) => valid.has(o.value));
    if (kept.length !== industries.length) onIndustriesChange(kept);
  }, [filteredIndustryOpts, permissionReady, industries, onIndustriesChange]);

  useEffect(() => {
    if (topicGroups.length === 0) return;
    const valid = new Set(topicGroups.flatMap((g) => (g.options ?? []).map((o) => o.value)));
    const kept = subTopics.filter((o) => valid.has(o.value));
    if (kept.length !== subTopics.length) onSubTopicsChange(kept);
  }, [topicGroups, subTopics, onSubTopicsChange]);

  // Prune orphaned Asset selections after the Asset options change (either the row
  // set loaded, or the Industry/UC/Topic scope narrowed assetOpts). Gate on the
  // underlying ROWS being loaded — NOT on assetOpts being non-empty: with scoping,
  // an empty assetOpts is a legitimate state (e.g. Industry+Topic contradiction),
  // and in that case out-of-scope selected assets SHOULD be pruned, not skipped.
  useEffect(() => {
    if (assetRows.length === 0) return;
    const valid = new Set(assetOpts.map((o) => o.value));
    const kept = assets.filter((o) => valid.has(o.value));
    if (kept.length !== assets.length) onAssetsChange(kept);
  }, [assetRows, assetOpts, assets, onAssetsChange]);

  return (
    <div className="ivt-motion-filters">
      <FilterField label="Industry">
        <Multiselect
          selectedOptions={industries}
          onChange={({ detail }) => onIndustriesChange(detail.selectedOptions)}
          options={filteredIndustryOpts}
          inlineTokens
          enableSelectAll
          i18nStrings={SELECT_ALL_I18N}
          filteringType="auto"
          placeholder="All"
          ariaLabel="Industry"
        />
      </FilterField>

      <FilterField label="Use Case">
        <Multiselect
          selectedOptions={useCases}
          onChange={({ detail }) => onUseCasesChange(detail.selectedOptions)}
          options={ucOptions}
          inlineTokens
          enableSelectAll
          i18nStrings={SELECT_ALL_I18N}
          filteringType="auto"
          placeholder="All"
          ariaLabel="Use Case"
        />
      </FilterField>

      <FilterField label="Topic">
        <Multiselect
          selectedOptions={subTopics}
          onChange={({ detail }) => onSubTopicsChange(detail.selectedOptions)}
          options={topicGroups}
          inlineTokens
          enableSelectAll
          i18nStrings={SELECT_ALL_I18N}
          filteringType="auto"
          placeholder="All"
          ariaLabel="Topic"
        />
      </FilterField>

      <FilterField label="Asset">
        <Multiselect
          selectedOptions={assets}
          onChange={({ detail }) => onAssetsChange(detail.selectedOptions)}
          options={assetOpts}
          inlineTokens
          enableSelectAll
          i18nStrings={SELECT_ALL_I18N}
          filteringType="auto"
          placeholder="All"
          ariaLabel="Asset"
        />
      </FilterField>

      {/* The two Motion-related filters sit AFTER Asset (product ordering): the
          Motion multiselect (specific Motions) then Has Linked Motion (claim tri-state). */}
      <FilterField label="Motion">
        <Multiselect
          selectedOptions={motions}
          onChange={({ detail }) => onMotionsChange(detail.selectedOptions)}
          options={motionOptions}
          inlineTokens
          enableSelectAll
          i18nStrings={SELECT_ALL_I18N}
          filteringType="auto"
          placeholder="All"
          ariaLabel="Motion"
        />
      </FilterField>

      {/* Has Linked Motion — Pipeline tab only (rendered iff the handler is supplied).
          Tri-state single-select → the backend hasMotion param. Tagged pipeline can
          be motion-claimed or tag-only, so this lets users isolate either. Named
          "Has Linked Motion" (not "Motion") to disambiguate from the Motion picker above. */}
      {onHasMotionChange && (
        <FilterField label="Has Linked Motion">
          <Select
            selectedOption={
              HAS_MOTION_OPTIONS.find((o) => o.value === hasMotionOptionId(hasMotion)) ?? HAS_MOTION_OPTIONS[0]
            }
            onChange={({ detail }) => onHasMotionChange(HAS_MOTION_TO_VALUE[detail.selectedOption.value ?? 'all'])}
            options={HAS_MOTION_OPTIONS}
            ariaLabel="Has Linked Motion"
          />
        </FilterField>
      )}

      {/* SFDC Stage AND Engagement Depth Score both moved to the Pipeline View's
          PipelinePropertyFilter (each is now a field of the SFDC property tree —
          SFDC_STAGE / PIPELINE_SCORE) — no longer Multiselects in this row. */}
    </div>
  );
}
